/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useRef, useState } from 'react';
import InputMask from 'react-input-mask';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import ConlicitasegSignupMessage from 'components/molecules/conlicitaseg_signup_message';
import ConlicitasegSignupLayout from 'components/organisms/conlicitaseg_signup_layout';
import { buildTracking, campaignParamsToQueryString } from 'services/tracking_params';
import { ApplicationState } from 'store';
import { registerConlicitaseg } from 'store/modules/profile_store/actions';
import { clearRegister, fetchCompanyByCnpj } from 'store/modules/register_store/actions';
import { RegisterState } from 'store/modules/register_store/types';
import { validateCnpj } from 'utils/cnpj';
import { countryCodeSafeMaskProps, isCelularValid, splitBrPhone } from 'utils/phone';

import { PasswordField } from './styles';

const ConlicitasegSignup: React.FC = () => {
  const dispatch = useDispatch();
  const { company, request } = useSelector<ApplicationState, RegisterState>(
    (state) => state.session.register,
  );

  const [name, setName] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [email, setEmail] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [outcome, setOutcome] = useState<'form' | 'existing' | 'email_sent'>('form');
  const submittingRef = useRef(false);

  useEffect(() => {
    if (company?.razao_social) setCompanyName(company.razao_social);
  }, [company?.razao_social]);

  const invalid = {
    name: !name.trim(),
    cnpj: !validateCnpj(cnpj),
    companyName: !companyName.trim(),
    email: !/^\S+@\S+\.\S+$/.test(email.trim()),
    whatsapp: !isCelularValid(whatsapp),
    password: password.trim().length < 6,
  };

  const fieldClass = (isInvalid: boolean) =>
    `form-control ${submitted && isInvalid ? 'is-invalid' : ''}`;

  const fetchCompany = () => {
    if (validateCnpj(cnpj)) dispatch(fetchCompanyByCnpj(cnpj, email));
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    if (Object.values(invalid).some(Boolean) || submittingRef.current) return;

    submittingRef.current = true;
    setLoading(true);
    setSubmitError(false);

    const phone = splitBrPhone(whatsapp);

    const result: any = await dispatch(
      registerConlicitaseg({
        client: {
          cnpj,
          razao_social: companyName.trim(),
          url_params: campaignParamsToQueryString(),
        },
        contact: {
          nome: name.trim(),
          email: email.trim(),
          ddd_celular: phone.ddd,
          numero_celular: phone.numero,
          senha: password,
        },
        tracking: buildTracking(),
      }),
    );

    if (!result || result.status >= 400) {
      submittingRef.current = false;
      setSubmitError(true);
      setLoading(false);
      return;
    }

    dispatch(clearRegister());

    setOutcome(result.data?.existing ? 'existing' : 'email_sent');
  };

  return (
    <ConlicitasegSignupLayout>
      {outcome === 'existing' && (
        <ConlicitasegSignupMessage icon="check_circle" title="Recebemos seu cadastro">
          Entraremos em contato em breve.
        </ConlicitasegSignupMessage>
      )}
      {outcome === 'email_sent' && (
        <ConlicitasegSignupMessage icon="mark_email_read" title="Confirme seu e-mail">
          Enviamos um link de validação para <strong>{email.trim()}</strong>. Clique nele para
          liberar seu acesso e fazer suas cotações de Seguro Garantia.
        </ConlicitasegSignupMessage>
      )}
      {outcome === 'form' && (
        <>
          <h3 className="text-center font-weight-bold mb-2">Cadastre-se gratuitamente</h3>
          <p className="text-center mb-4">
            O ConlicitaSeg é uma ferramenta do Grupo Conlicitação. Crie sua conta em poucos minutos
            e faça a cotação do seu seguro garantia agora mesmo.
          </p>

          <div className="form-group">
            <label htmlFor="conlicitaseg-signup-name">Nome</label>
            <input
              id="conlicitaseg-signup-name"
              className={fieldClass(invalid.name)}
              placeholder="Seu nome completo"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <div className="invalid-feedback">Informe seu nome.</div>
          </div>

          <div className="form-row">
            <div className="form-group col-md-6">
              <label htmlFor="conlicitaseg-signup-cnpj">CNPJ</label>
              <InputMask
                id="conlicitaseg-signup-cnpj"
                mask="**.***.***/****-99"
                className={fieldClass(invalid.cnpj)}
                placeholder="00.000.000/0000-00"
                value={cnpj}
                onBlur={fetchCompany}
                onChange={(e) => setCnpj(e.target.value.toUpperCase())}
              />
              <div className="invalid-feedback">CNPJ inválido.</div>
            </div>
            <div className="form-group col-md-6">
              <label htmlFor="conlicitaseg-signup-company">Razão Social</label>
              <input
                id="conlicitaseg-signup-company"
                className={fieldClass(invalid.companyName)}
                placeholder="Ex. Empresa LTDA."
                autoComplete="organization"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />
              <div className="invalid-feedback">Informe a razão social.</div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group col-md-6">
              <label htmlFor="conlicitaseg-signup-email">E-mail</label>
              <input
                id="conlicitaseg-signup-email"
                type="email"
                className={fieldClass(invalid.email)}
                placeholder="email@email.com.br"
                autoComplete="email"
                value={email}
                onBlur={fetchCompany}
                onChange={(e) => setEmail(e.target.value)}
              />
              <div className="invalid-feedback">E-mail inválido.</div>
            </div>
            <div className="form-group col-md-6">
              <label htmlFor="conlicitaseg-signup-whatsapp">WhatsApp</label>
              <InputMask
                {...countryCodeSafeMaskProps}
                id="conlicitaseg-signup-whatsapp"
                mask="(99) 99999-9999"
                className={fieldClass(invalid.whatsapp)}
                placeholder="(00) 00000-0000"
                autoComplete="tel"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
              />
              <div className="invalid-feedback">WhatsApp inválido.</div>
            </div>
          </div>

          <div className="form-group mb-4">
            <label htmlFor="conlicitaseg-signup-password">Senha</label>
            <PasswordField>
              <input
                id="conlicitaseg-signup-password"
                type={showPassword ? 'text' : 'password'}
                className={fieldClass(invalid.password)}
                placeholder="Sua senha"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-link"
                onClick={() => setShowPassword((current) => !current)}
                aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
              >
                <span className="material-icons" aria-hidden="true">
                  {showPassword ? 'visibility_off' : 'visibility'}
                </span>
              </button>
            </PasswordField>
            {submitted && invalid.password && (
              <div className="invalid-feedback d-block">Senha deve ter no mínimo 6 caracteres.</div>
            )}
          </div>

          {submitError && (
            <div className="alert alert-danger" role="alert">
              Não foi possível concluir o cadastro. Tente novamente.
            </div>
          )}

          <button
            type="button"
            className="btn btn-primary btn-block"
            onClick={handleSubmit}
            disabled={loading || request.fetch_company.loading}
          >
            Iniciar cotação
          </button>

          <div className="text-center my-3">
            <Link to="/">Já tenho uma conta</Link>
          </div>

          <small className="d-block text-center">
            Ao confirmar o cadastro, concordo com os{' '}
            <a href="https://conlicitacao.com.br/termos-de-uso" target="_blank" rel="noreferrer">
              termos de uso
            </a>{' '}
            e{' '}
            <a
              href="https://conlicitacao.com.br/politica-de-privacidade/"
              target="_blank"
              rel="noreferrer"
            >
              política de privacidade
            </a>
            .
          </small>
        </>
      )}
    </ConlicitasegSignupLayout>
  );
};

export default ConlicitasegSignup;
